import { useTranslation } from "react-i18next"
import { observer } from "mobx-react-lite"
import { useStore } from "../contexts/store"
import { useParams } from "../stores/uiStore"
import { Pod } from "../shared/src/types/Pod"
import Logo from "./Elements/Logo"
import ConversationUnreads from "./Pod/Conversation/ConversationUnreads"
import NavigationButton from "./Elements/NavigationButton"
import UxIcon from "./Elements/UxIcon"

import { Box, Button, Divider } from "@mui/material"
import Clicktip from "./Elements/Clicktip"
import { useLocation } from "wouter"



const Sidebar = () => {
  const { t } = useTranslation()
  const { podStore, sessionStore, uiStore } = useStore()
  const { nodeId, podId, subPage, tab } = useParams()
  const pods = sessionStore.getSortedPods()
  const [location, navigate] = useLocation()

  // the drawer should not be larger than the width of the screen
  const width = uiStore.width
  const drawerWidth = 320 < width ? 320 : width


  const Navigation = observer(() => {
    const activePod = pods.find(pod => pod.podId === podId)
    // if pod is open: show pod content
    // else: show list of pod links
    return (
      <Box>
        <NavigationButton variant={activePod ? "activeParent" : (subPage ? "inactive" : "active")} icon={<UxIcon id={"home"} fontSize="small" />} label={podId ? t("Back to my Pods") : t("My Pods")} to={`/`} />
        <Box sx={{margin: "5px 0 5px 10px"}}>
          {activePod ?
            <AcitvePodNavigation activePod={activePod} />
            :
            <Box sx={{display: "grid", gap: "2px"}}>
              {pods.map(pod => (
                <NavigationButton
                  key={pod.podId}
                  icon={<UxIcon id={"pod"} fontSize="small" />}
                  label={pod.name}
                  to={`/pod/${pod.podId}`}
                  variant={subPage ? "inactive" : "activeChild"}
                />
              ))}
            </Box>
          }
        </Box>
      </Box>
    )
  })

  const AcitvePodNavigation = observer(({activePod}: {activePod: Pod}) => {
    return (
      <Box>
        <NavigationButton variant={tab ? "activeParent" : "active"} icon={<UxIcon id={"pod"} fontSize="small" />} label={activePod.name} to={`/pod/${podId}`} />
        <Box sx={{display: "grid", gap: "2px", margin: "5px 0 5px 20px"}}>
          <NavigationButton
            icon={<UxIcon id={"podMaterial"} fontSize="small" />}
            label={t("Material")}
            to={`/pod/${podId}/contents`}
            variant={tab === "contents" ? "active" : ((tab === "pdf" && nodeId) ? "activeParent" : "activeChild")}
          />
          {(tab === "pdf" && nodeId) &&
            <Box sx={{margin: "5px 0 5px 20px"}}>
              <NavigationButton
                icon={<UxIcon id={"podFile"} fontSize="small" />}
                label={podStore.getFilename(nodeId) as string}
                to={`/pod/${podId}/pdf/${nodeId}`}
                variant={"active"}
              />
            </Box>
          }
          <NavigationButton
            badge={<ConversationUnreads />}
            icon={<UxIcon id={"podConversations"} fontSize="small" />}
            label={t("Conversations")}
            to={`/pod/${podId}/conversations`}
            variant={tab === "conversations" ? "active" : "activeChild"}
          />
          <NavigationButton
            icon={<UxIcon id={"podNotes"} fontSize="small" />}
            label={t("Notes")}
            to={`/pod/${podId}/notes`}
            variant={tab === "notes" ? "active" : "activeChild"}
          />
          <NavigationButton
            icon={<UxIcon id={"podActivity"} fontSize="small" />}
            label={t("Activity")}
            to={`/pod/${podId}/activity`}
            variant={tab === "activity" ? "active" : "activeChild"}
          />
          <NavigationButton
            icon={<UxIcon id={"podReadingQuestions"} fontSize="small" />}
            label={t("Reading questions")}
            to={`/pod/${podId}/questions`}
            variant={tab === "questions" ? "active" : "activeChild"}
          />
          <NavigationButton
            icon={<UxIcon id={"podTags"} fontSize="small" />}
            label={t("Tags")}
            to={`/pod/${podId}/tags`}
            variant={tab === "tags" ? "active" : "activeChild"}
          />
        </Box>
        <Box sx={{display: "grid", gap: "2px", margin: "15px 0 5px 5px"}}>
          <NavigationButton
            icon={<UxIcon id={"podPeople"} fontSize="small" />}
            label={t("People")}
            to={`/pod/${podId}/people`}
            variant={tab === "people" ? "active" : "activeChild"}
          />
          <NavigationButton
            icon={<UxIcon id={"podSettings"} fontSize="small" />}
            label={t("Settings")}
            to={`/pod/${podId}/settings`}
            variant={tab === "settings" ? "active" : "activeChild"}
          />
        </Box>
      </Box>
    )
  })

  const Helpdesk = () => (
    <Box sx={{
      backgroundColor: "primary.main",
      borderRadius: "17px",
      display: "grid",
      fontSize: "14px",
      padding: "15px",
      marginTop: "10px"
    }}>
      <Box sx={{
        display: "grid",
        gap: "10px",
        gridTemplateColumns: "max-content max-content"
      }}>
        <Box sx={{padding: "5px 10px"}}>
          <UxIcon id={"help"} sx={{backgroundColor: "white", borderRadius: "12px", color: "primary.main"}} />
        </Box>
        <Box>
          <Box sx={{color: "white", fontWeight: 600}}>
            {t('Do you need help?')}
          </Box>
          <Box sx={{color: "white", fontWeight: 300}}>
            {t('Visit our tutorial')}
          </Box>
        </Box>
      </Box>
      <Button onClick={() => { window.open('https://www.shrimpp.de/hilfe', '_blank') }} /*onClick={() => { navigate('/help', { state: { prevUrl: location } }) }}*/ sx={{backgroundColor: "white", borderRadius: "12px", color: "textcolor.main", fontSize: "12px", marginTop: "15px"}}>
        {t('Open the help section')}
      </Button>
    </Box>
  )

  return(
    <Box sx={{
      alignContent: "start",
      backgroundColor: "back.main",
      display: "grid",
      gap: "20px",
      height: "100%",
      overflowY: "auto",
      padding: "20px",
      width: drawerWidth
    }}>
      <Logo />
      <Divider variant="middle"/>
      <Navigation />
      <Helpdesk />
    </Box>
  )
}

export default observer(Sidebar)
